import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
// import Logger from './utils/logger';
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import useAutoLogout from "./hooks/useAutoLogout";
// Layouts
import AppLayout from "./layout/AppLayout";
import AuthLayout from "./layout/AuthLayout";

// Auth Pages
import SignIn from "./pages/AuthPages/SignIn";
import Logout from "./pages/AuthPages/Logout";
import ForgotPassword from "./pages/AuthPages/ForgotPassword";
import ResetPassword from "./pages/AuthPages/ResetPassword";
import CheckEmail from "./pages/AuthPages/CheckEmail";
import SuccessPage from "./pages/AuthPages/SuccessPage";

// Protected Route
import ProtectedRoute from "./components/ProtectedRoute";

// Dashboard & UI
import Ecommerce from "./pages/Dashboard/ECommerce";
import Blank from "./pages/Blank";
import Alerts from "./pages/UiElements/Alerts";
import Avatars from "./pages/UiElements/Avatars";
import Badges from "./pages/UiElements/Badges";
import Buttons from "./pages/UiElements/Buttons";
import Images from "./pages/UiElements/Images";
import NotFound from "./pages/OtherPage/NotFound";

// User
import UserAdd from "./pages/User/UserAdd";
import UserEdit from "./pages/User/UserEdit";
import UserProfiles from "./pages/UserProfiles";
import UserTable from "./components/tables/UserTable";

// Settings
import SettingList from "./pages/Settings/SettingList";
// import DuplicateSetting from "./pages/Settings/DuplicateSetting";

// Job Title
import JobTitleTable from "./components/tables/JobTitleTable";
import JobTitleAdd from "./pages/JobTitle/JobTitleAdd";
import JobTitleEdit from "./pages/JobTitle/JobTitleEdit";

// Source
import SourceTable from "./components/tables/SourceTable";
import SourceAdd from "./pages/Source/SourceAdd";
import SourceEdit from "./pages/Source/SourceEdit";

// Stage
import StageTable from "./components/tables/StageTable";
import StageAdd from "./pages/Stage/StageAdd";
import StageEdit from "./pages/Stage/StageEdit";

// Status
import StatusTable from "./components/tables/StatusTable";
import StatusAdd from "./pages/Status/StatusAdd";
import StatusEdit from "./pages/Status/StatusEdit";

// Leads
import LeadTable from "./components/tables/LeadTable";
import LeadAdd from "./pages/Lead/LeadAdd";
import LeadEdit from "./pages/Lead/LeadEdit";
import ViewLead from "./pages/Lead/ViewLead";
import LeadCall from "./pages/Lead/LeadCall";
import MeetingTable from "./components/tables/MeetingTable";
import TaskTable from "./components/tables/TaskTable";

// Reports
import LeadSummary from "./pages/Reports/LeadSummary";
import LeadSummaryDetails from "./pages/Reports/LeadSummaryDetails";

// Category / Product
import CategoryTable from "./components/tables/CategoryTable";
import CategoryAdd from "./pages/Category/CategoryAdd";
import CategoryEdit from "./pages/Category/CategoryEdit";
import SubCategoryTable from "./components/tables/SubCategoryTable";
import SubCategoryAdd from "./pages/SubCategory/SubCategoryAdd";
import SubCategoryEdit from "./pages/SubCategory/SubCategoryEdit";
import ProductTable from "./components/tables/Product";
import ProductAdd from "./pages/Product/ProductAdd";
import ProductEdit from "./pages/Product/ProductEdit";
import ProductView from "./pages/Product/ProductView";

// Opportunity
import OpportunityAdd from "./pages/Opportunity/OpportunityAdd";
import OpportunityEdit from "./pages/Opportunity/OpportunityEdit";
import OpportunityView from "./pages/Opportunity/OpportunityView";
import OpportunityTable from "./components/tables/OpportunityTable";
import OpportunityQuotationView from "./pages/Opportunity/OpportunityQuotationView";

// Quotation
import QuotationAdd from "./pages/Quotation/QuotationAdd";
import QuotationEdit from "./pages/Quotation/QuotationEdit";
import QuotationView from "./pages/Quotation/QuotationView";
import QuotesTable from "./components/tables/QuotesTable";

// Others
import CompanyDetails from "./pages/Company/CompanyDetails";
import ContactTable from "./components/tables/ContactTable";
import ContactDetails from "./components/tables/ContactDetails";
import CallTable from "./components/tables/CallTable";

// Chat
import Chat from "../src/components/tables/Chat";

// InnerApp is where we use useSettings
function InnerApp() {
  const { settings } = useSettings();
  useAutoLogout();

  // Dynamically update favicon
useEffect(() => {
  const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
  const logoPath = settings.logoPath;
  const isValid = logoPath && !logoPath.includes("null") && logoPath.trim() !== "";

  if (favicon && isValid) {
    favicon.href = `${logoPath}?v=${Date.now()}`; // prevent cache
  }
}, [settings.logoPath]);

  // Set dynamic document title
  useEffect(() => {
    const fallbackTitle = "Powered by KloudData";

    const name = typeof settings.logoName === "string" ? settings.logoName.trim() : "";
    const isValid = name !== "" && name.toLowerCase() !== "null";

    document.title = isValid
      ? `${name.trim()} | ${fallbackTitle}`
      : fallbackTitle;
  }, [settings.logoName]);

  return (
    <Routes>
      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout key={settings.logoPath} />}>
          <Route index path="/" element={<Ecommerce />} />
          <Route path="/profile" element={<UserProfiles />} />
          <Route path="/blank" element={<Blank />} />
          <Route index path="/lead-summary" element={<LeadSummary />} />
          <Route path="/lead-summary-details" element={<LeadSummaryDetails />} />

          {/* Add Chat route here */}
          <Route path="/chat" element={<Chat />} />

          {/* Forms */}
          <Route path="/user-add" element={<UserAdd />} />
          <Route path="/user-edit/:id" element={<UserEdit />} />
          <Route path="/job-title-add" element={<JobTitleAdd />} />
          <Route path="/job-title-edit/:id" element={<JobTitleEdit />} />
          <Route path="/source-add" element={<SourceAdd />} />
          <Route path="/source-edit/:id" element={<SourceEdit />} />
          <Route path="/stage-add" element={<StageAdd />} />
          <Route path="/stage-edit/:id" element={<StageEdit />} />
          <Route path="/quotation-add" element={<QuotationAdd />} />
          <Route path="/quotation-edit/:quotationId" element={<QuotationEdit />} />
          <Route path="/quotation-view" element={<QuotationView />} />
          <Route path="/status-add" element={<StatusAdd />} />
          <Route path="/status-edit/:id" element={<StatusEdit />} />
          <Route path="/opportunity-add" element={<OpportunityAdd />} />
          <Route path="/opportunity-edit/:id" element={<OpportunityEdit />} />
          <Route path="/opportunity-view" element={<OpportunityView />} />
          <Route path="/opportunity-quotation-view" element={<OpportunityQuotationView />} />
          <Route path="/category-add" element={<CategoryAdd />} />
          <Route path="/category-edit/:id" element={<CategoryEdit />} />
          <Route path="/subcategory-add" element={<SubCategoryAdd />} />
          <Route path="/subcategory-edit/:id" element={<SubCategoryEdit />} />
          <Route path="/lead-add" element={<LeadAdd />} />
          <Route path="/lead-edit/:id" element={<LeadEdit />} />
          <Route path="/product-add" element={<ProductAdd />} />
          <Route path="/product-edit/:id" element={<ProductEdit />} />
          <Route path="/product-view/:id" element={<ProductView />} />
          <Route path="/settings-list" element={<SettingList />} />
          {/* <Route path="/duplicate-setting" element={<DuplicateSetting />} /> */}

          {/* View Pages */}
          <Route path="/company-details" element={<CompanyDetails />} />
          <Route path="/lead-view" element={<ViewLead />} />
          <Route path="/lead-meeting/:lead_id" element={<MeetingTable />} />
          <Route path="/lead-call" element={<LeadCall />} />
          <Route path="/lead-task/:lead_id" element={<TaskTable />} />

          {/* Tables */}
          <Route path="/user-list" element={<UserTable />} />
          <Route path="/contact-list" element={<ContactTable />} />
          <Route path="/contact-details" element={<ContactDetails />} />
          <Route path="/job-title-list" element={<JobTitleTable />} />
          <Route path="/source-list" element={<SourceTable />} />
          <Route path="/stage-list" element={<StageTable />} />
          <Route path="/status-list" element={<StatusTable />} />
          <Route path="/lead-list" element={<LeadTable />} />
          <Route path="/opportunity-list" element={<OpportunityTable />} />
          <Route path="/category-list" element={<CategoryTable />} />
          <Route path="/subcategory-list" element={<SubCategoryTable />} />
          <Route path="/call-list" element={<CallTable />} />
          <Route path="/call-list/:lead_id" element={<CallTable />} />
          <Route path="/product-list" element={<ProductTable />} />
          <Route path="/quotation-list" element={<QuotesTable />} />

          {/* UI Elements */}
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/avatars" element={<Avatars />} />
          <Route path="/badges" element={<Badges />} />
          <Route path="/buttons" element={<Buttons />} />
          <Route path="/images" element={<Images />} />
        </Route>
      </Route>

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/reset-success" element={<SuccessPage />} />
      </Route>

      {/* 404 Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Outer wrapper
export default function App() {
  return (
    <>
      <ToastContainer position="bottom-right" autoClose={1000} />
      <Router>
        <SettingsProvider>
          <InnerApp />
        </SettingsProvider>
      </Router>
    </>
  );
}